import { AbstractProgram } from "@kvix/shared";
import { KvixIcon, Text } from "@kvix/ui";
import { Fab, Fade, makeStyles } from "@material-ui/core";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { animated, useSpring } from "react-spring";
import styled from "styled-components";

interface Props {
  program: AbstractProgram;
}

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    paddingBottom: "56.25%",
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  ratio: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,

    display: "flex",
    alignItems: "center",
  },
  videoContainer: {
    position: "relative",
    display: "flex",
    height: "100%",
    width: "100%",
  },
  videoOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,

    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",

    color: theme.palette.common.white,
    paddingBottom: theme.spacing(4),
    background:
      "linear-gradient(0deg, rgba(0, 0, 0, 1) -50%, transparent 100%)",
  },
  video: {
    width: "100%",
    height: "100%",
    "&:focus": {
      outline: "none",
    },
  },
  previewIndicator: {
    color: theme.palette.common.white,
    position: "absolute",
    top: "10px",
    left: "10px",
    background: "rgba(27,36,45,0.5)",
    padding: "4px 6px",
    borderRadius: "6px",
    userSelect: "none",
  },
  previewIndicatorText: {
    color: theme.palette.common.white,
    textTransform: "uppercase",
    fontWeight: 700,
    fontSize: "12px",
    letterSpacing: "1.25px",

    [theme.breakpoints.down("sm")]: {
      fontSize: "8px",
    },
  },
}));

const PlayButton = styled(Fab)`
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  position: absolute;

  &.MuiFab-root {
    color: #fff;
    height: 40px;
    width: 40px;
    margin: auto;
    box-shadow: none;

    svg {
      height: 1.4em;
      width: 1.4em;
    }
  }
`;
export const ProgramIntroductionVideo: React.FC<Props> = (props) => {
  const classes = useStyles(props);
  const video = useRef<HTMLVideoElement>();

  const [playing, setPlaying] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(false);
    setPlaying(false);
    video.current?.load();
    setTimeout(() => setLoaded(true), 100);
  }, [props.program]);

  const style = useSpring({
    config: {
      tension: 150,
      friction: 15,
    },
    to: {
      opacity: loaded ? 1 : 0,
      transform: loaded ? "translate3d(0, 0, 0)" : "translate3d(0, 5px, 0)",
    },
  });

  const play = async () => {
    await video.current.play();
    setPlaying(true);
    video.current.focus();
  };

  const pause = async () => {
    video.current.pause();
    setPlaying(false);
  };

  const { t, ready } = useTranslation("program");

  if (!ready) {
    return null;
  }

  return (
    <animated.div style={style}>
      <div className={classes.root}>
        <div className={classes.ratio}>
          <div className={classes.videoContainer}>
            <video
              onBlur={pause}
              ref={video}
              className={classes.video}
              controls={playing}
              playsInline
            >
              <source src={props.program.videoUrl} type="video/mp4" />
            </video>

            <div className={classes.previewIndicator}>
              <Text className={classes.previewIndicatorText}>
                {t("videoPreview")}
              </Text>
            </div>
            <Fade in={loaded && !playing}>
              <div className={classes.videoOverlay}>
                <PlayButton color="primary" onClick={play}>
                  <KvixIcon.Play />
                </PlayButton>
              </div>
            </Fade>
          </div>
        </div>
      </div>
    </animated.div>
  );
};
